<template>
  <div class="flex flex-wrap py-1.5">
    <ImageGroup
      :images="previews"
      :remove="!readonly"
      class="last-of-type:mr-1"
      @remove="handleRemoveMedia"
      @view="handleViewMedia"
    />
    <FileInput
      v-if="!readonly"
      ref="mediaInput"
      :accept="[
        'image/png',
        'image/jpg',
        'image/jpeg',
        'video/mp4',
        'video/quicktime',
        'video/ogg',
        'video/webm',
      ]"
      multiple
      hidden
      @input="handleMediaSelected"
    />
    <IconButton
      v-if="!readonly"
      variant="secondary"
      type="gray"
      size="sm"
      :loading="busy"
      class="h-12 w-10 rounded-md"
      @click="mediaInput?.click()"
    >
      <Icon name="plus" />
    </IconButton>
    <MediaViewer
      v-model:open="openMediaViewer"
      :title="$t('components.task_details.media.title')"
      :description="$t('components.task_details.media.description')"
      :src="activeMedia?.url"
      :type="activeMedia?.mime_type"
      :multiple="media.length > 1"
      @next="next"
      @previous="previous"
    />
  </div>
</template>

<script setup>
import route from "ziggy-js";
import { computed, ref, watch, inject } from "vue";
import { router } from "@inertiajs/vue3";
import { IconButton } from "@/SharedComponents/Buttons";
import { Icon } from "@/SharedComponents/Icon";
import { ImageGroup } from "@/SharedComponents/Images";
import { FileInput } from "@/SharedComponents/Inputs";
import { MediaViewer } from "@/SharedComponents/Media";
import { toast } from "@/SharedComponents/Toaster";

const mediaInput = ref(null);

const props = defineProps({
  task: { type: Object, required: true },
  readonly: { type: Boolean, required: false },
});

const emitter = inject("emitter");

const activeIndex = ref(0);
const openMediaViewer = ref(undefined);

const media = computed(() => props.task.problem?.photos || []);

const previews = computed(() =>
  media.value.map((item) => ({
    id: item.id,
    url: item.thumbnail_url || item.url,
  }))
);

const activeMedia = computed(() => media.value[activeIndex.value]);

function handleViewMedia(index) {
  activeIndex.value = index;
  openMediaViewer.value = true;
}

function next() {
  activeIndex.value += 1;
  if (activeIndex.value === media.value.length) {
    activeIndex.value = 0;
  }
}

function previous() {
  activeIndex.value -= 1;
  if (activeIndex.value < 0) {
    activeIndex.value = media.value.length - 1;
  }
}

const busy = ref(false);

function handleMediaSelected(images) {
  if (props.readonly) {
    return;
  }

  const data = new FormData();

  images.forEach((image, i) => {
    data.append(`photos[${i}]`, image, image.name);
  });

  router.post(route("tasks.photos", [props.task]), data, {
    onStart: () => (busy.value = true),
    onSuccess: () => {
      emitter.emit("reload-task-details", props.task);
    },
    onFinish: () => (busy.value = false),
  });
}

function handleRemoveMedia(id) {
  if (props.readonly) {
    return;
  }
  router.delete(route("tasks.photo.delete", [id]), {
    onSuccess: () => {
      emitter.emit("reload-task-details", props.task);
    },
  });
}

watch(
  () => mediaInput.value?.errors,
  (errors) => {
    if (errors) {
      Object.values(errors).forEach((error) => {
        toast.error(error, { duration: 8000 });
      });
    }
  }
);
</script>
